/**
 * Pickup Service
 *
 * Handles pickup folder and user management with real API integration.
 */

import apiClient from './api';

/* ── Pickup Folders ── */

export const getPickupFolders = async (params = {}) => {
  try {
    const { search = '', page = 1, limit = 10 } = params;
    const response = await apiClient.get('/pickup-folders', {
      params: { search, page, page_size: limit }
    });
    const apiData = response.data?.data || response.data;
    const folderList = apiData?.pickup_folders || [];
    const data = folderList.map(f => ({
      id: f.ID,
      name: f.NAME,
      description: f.DESCRIPTION || '',
      s3Prefix: f.S3_PREFIX || '',
      retentionDays: f.RETENTION_DAYS || 30,
      isActive: f.IS_ACTIVE !== undefined ? f.IS_ACTIVE : true,
      reportCount: f.REPORT_COUNT || 0,
      userCount: f.USER_COUNT || 0,
      createdAt: f.CREATED_AT || '',
      archiveEnabled: f.ARCHIVE_ENABLED || false,
      viewerEmails: f.VIEWER_EMAILS || []
    }));
    return {
      data,
      pagination: {
        page: apiData?.pagination?.page || page,
        limit: apiData?.pagination?.page_size || limit,
        total: apiData?.pagination?.total || 0,
        totalPages: apiData?.pagination?.total_pages || 1
      }
    };
  } catch (error) {
    console.error('Failed to fetch pickup folders:', error);
    throw error;
  }
};

export const getAllPickupFolders = async () => {
  try {
    const response = await apiClient.get('/pickup-folders', {
      params: { page: 1, page_size: 100 }
    });
    const apiData = response.data?.data || response.data;
    const folderList = apiData?.pickup_folders || [];
    return folderList
      .map(f => ({
        id: f.ID,
        name: f.NAME,
        description: f.DESCRIPTION || '',
        isActive: f.IS_ACTIVE !== undefined ? f.IS_ACTIVE : true
      }))
      .filter(f => f.isActive);
  } catch (error) {
    console.error('Failed to fetch all pickup folders:', error);
    throw error;
  }
};

export const getPickupFolderById = async (folderId) => {
  try {
    const response = await apiClient.get(`/pickup-folders/${folderId}`);
    const f = response.data?.data || response.data;
    return {
      id: f.ID,
      name: f.NAME,
      description: f.DESCRIPTION || '',
      s3Prefix: f.S3_PREFIX || '',
      retentionDays: f.RETENTION_DAYS || 30,
      isActive: f.IS_ACTIVE !== undefined ? f.IS_ACTIVE : true,
      reportCount: f.REPORT_COUNT || 0,
      userCount: f.USER_COUNT || 0,
      createdAt: f.CREATED_AT || '',
      archiveEnabled: f.ARCHIVE_ENABLED || false,
      viewerEmails: f.VIEWER_EMAILS || []
    };
  } catch (error) {
    console.error('Failed to fetch pickup folder:', error);
    throw error;
  }
};

export const createPickupFolder = async (data) => {
  try {
    const payload = {
      name: data.name,
      description: data.description || '',
      retention_days: parseInt(data.retentionDays, 10) || 30,
      archive_enabled: data.archiveEnabled || false,
      viewer_emails: data.viewerEmails || []
    };
    const config = {};
    if (data.teamId) {
      config.headers = { 'X-Active-Team-Id': data.teamId };
    }
    const response = await apiClient.post('/pickup-folders', payload, config);
    const apiData = response.data?.data || response.data;
    return { data: { id: apiData.id, name: apiData.name } };
  } catch (error) {
    console.error('Failed to create pickup folder:', error);
    throw error;
  }
};

export const updatePickupFolder = async (folderId, data) => {
  try {
    const payload = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.description !== undefined) payload.description = data.description;
    if (data.retentionDays !== undefined) payload.retention_days = parseInt(data.retentionDays, 10);
    if (data.isActive !== undefined) payload.is_active = data.isActive;
    if (data.archiveEnabled !== undefined) payload.archive_enabled = data.archiveEnabled;
    if (data.viewerEmails !== undefined) payload.viewer_emails = data.viewerEmails;
    await apiClient.put(`/pickup-folders/${folderId}`, payload);
    return { data: { id: folderId } };
  } catch (error) {
    console.error('Failed to update pickup folder:', error);
    throw error;
  }
};

export const deletePickupFolder = async (folderId) => {
  try {
    await apiClient.delete(`/pickup-folders/${folderId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to delete pickup folder:', error);
    throw error;
  }
};

export const getPickupFolderFiles = async (folderId) => {
  try {
    const response = await apiClient.get(`/pickup-folders/${folderId}/files`);
    const apiData = response.data?.data || response.data;

    const mapFile = (f) => ({
      key: f.KEY,
      filename: f.FILENAME,
      size: f.SIZE,
      lastModified: f.LAST_MODIFIED
    });

    // Map archived_files: dict keyed by date string → array of file objects
    const rawArchived = apiData?.archived_files || {};
    const archivedFiles = {};
    for (const [date, files] of Object.entries(rawArchived)) {
      archivedFiles[date] = (files || []).map(mapFile);
    }

    return {
      files: (apiData?.files || []).map(mapFile),
      archivedFiles,
      total: apiData?.total || 0,
      totalArchived: apiData?.total_archived || 0,
      folderName: apiData?.folder_name || ''
    };
  } catch (error) {
    console.error('Failed to fetch pickup folder files:', error);
    throw error;
  }
};

/* ── Pickup Users ── */

export const getPickupUsers = async (params = {}) => {
  try {
    const { search = '', page = 1, limit = 10 } = params;
    const response = await apiClient.get('/pickup-users', {
      params: { search, page, page_size: limit }
    });
    const apiData = response.data?.data || response.data;
    const userList = apiData?.pickup_users || [];
    const data = userList.map(u => ({
      id: u.ID,
      username: u.USERNAME,
      folderIds: u.FOLDER_IDS || [],
      folderNames: u.FOLDER_NAMES || [],
      isActive: u.IS_ACTIVE !== undefined ? u.IS_ACTIVE : true,
      createdAt: u.CREATED_AT || ''
    }));
    return {
      data,
      pagination: {
        page: apiData?.pagination?.page || page,
        limit: apiData?.pagination?.page_size || limit,
        total: apiData?.pagination?.total || 0,
        totalPages: apiData?.pagination?.total_pages || 1
      }
    };
  } catch (error) {
    console.error('Failed to fetch pickup users:', error);
    throw error;
  }
};

export const getPickupUserById = async (userId) => {
  try {
    const response = await apiClient.get(`/pickup-users/${userId}`);
    const u = response.data?.data || response.data;
    return {
      id: u.ID,
      username: u.USERNAME,
      folderIds: u.FOLDER_IDS || [],
      folderNames: u.FOLDER_NAMES || [],
      isActive: u.IS_ACTIVE !== undefined ? u.IS_ACTIVE : true,
      createdAt: u.CREATED_AT || ''
    };
  } catch (error) {
    console.error('Failed to fetch pickup user:', error);
    throw error;
  }
};

export const createPickupUser = async (data) => {
  try {
    const payload = {
      username: data.username,
      ssh_public_key: data.sshPublicKey,
      folder_ids: data.folderIds || []
    };
    const response = await apiClient.post('/pickup-users', payload);
    const apiData = response.data?.data || response.data;
    return { data: { id: apiData.id, username: apiData.username } };
  } catch (error) {
    console.error('Failed to create pickup user:', error);
    throw error;
  }
};

export const updatePickupUser = async (userId, data) => {
  try {
    const payload = {};
    if (data.username !== undefined) payload.username = data.username;
    if (data.sshPublicKey) payload.ssh_public_key = data.sshPublicKey;
    if (data.folderIds !== undefined) payload.folder_ids = data.folderIds;
    if (data.isActive !== undefined) payload.is_active = data.isActive;
    await apiClient.put(`/pickup-users/${userId}`, payload);
    return { data: { id: userId } };
  } catch (error) {
    console.error('Failed to update pickup user:', error);
    throw error;
  }
};

export const deletePickupUser = async (userId) => {
  try {
    await apiClient.delete(`/pickup-users/${userId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to delete pickup user:', error);
    throw error;
  }
};

export const downloadPickupFile = async (folderId, fileKey, mode = 'download') => {
  try {
    const response = await apiClient.get(`/pickup-folders/${folderId}/files/download`, {
      params: { key: fileKey, mode }
    });
    const apiData = response.data?.data || response.data;
    return {
      downloadUrl: apiData.download_url,
      fileKey: apiData.file_key,
      expiresIn: apiData.expires_in
    };
  } catch (error) {
    console.error('Failed to get download URL:', error);
    throw error;
  }
};
